import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@components';
import TabNavigation from '@components/TabNavigation';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import ActivityModal from '../activity-modal/ActivityModal';
import CalendarIntegrationDetailsModal from '../lead-settings/CalendarIntegrationDetailsModal';
import { SyncPreferences, SyncPreferenceType } from '../lead-settings/types';
import CallTable from './CallTable';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EventTable from './EventTable';
import TaskTable from './TaskTable';

import { ActivityTypeOptions } from '../../constants/leadActivity';
import {
  useDeleteLeadCallMutation,
  useDeleteLeadEventMutation,
  useDeleteLeadTaskMutation,
  useGetGoogleAuthStatusQuery,
  useGetLeadCallsQuery,
  useGetLeadEventsQuery,
  useGetLeadTasksQuery,
  useGetUserOrgSettingsQuery,
  useUpdateUserOrgSettingsMutation
} from '../../queries/LeadManagementQuery';
import {
  ActivityFormData,
  AnalyticsDataType,
  DeleteModalProps,
  Tab
} from '../../types/leadActivity';
import {
  getDeleteModalMessage,
  getDeleteModalTitle,
  getTaskStatus,
  splitTimestamp
} from '../../utils/leadActivity';

const ActivitiesTab: React.FC<{
  leadId: string;
  analyticsData?: AnalyticsDataType;
}> = ({ leadId, analyticsData }) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg } = useSelector((state: any) => state.user);

  const { createSuccessAlert, createErrorAlert } = useNotify();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const { leadDetails } = analyticsData ?? {};

  const { data: tasks } = useGetLeadTasksQuery({
    organizationId,
    leadId,
    page: currentPage - 1,
    limit: pageSize
  });
  const { data: events } = useGetLeadEventsQuery({
    organizationId,
    leadId,
    page: currentPage - 1,
    limit: pageSize
  });
  const { data: calls } = useGetLeadCallsQuery({
    organizationId,
    leadId,
    page: currentPage - 1,
    limit: pageSize
  });
  const [deleteTask] = useDeleteLeadTaskMutation();
  const [deleteEvent] = useDeleteLeadEventMutation();
  const [deleteCall] = useDeleteLeadCallMutation();
  const { data: userOrgSettingsData, refetch: refetchSettings } =
    useGetUserOrgSettingsQuery({ organizationId }, { skip: !organizationId });
  const { data: googleAuthData } = useGetGoogleAuthStatusQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const [updateUserOrgSettings, { isLoading: isUpdating }] =
    useUpdateUserOrgSettingsMutation();

  const [activeTab, setActiveTab] = useState<ActivityTypeOptions>(
    ActivityTypeOptions.TASK
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ActivityTypeOptions>(
    ActivityTypeOptions.TASK
  );
  const [editingItem, setEditingItem] = useState<ActivityFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<DeleteModalProps | null>(
    null
  );
  const [
    showCalendarIntegrationDetailsModal,
    setShowCalendarIntegrationDetailsModal
  ] = useState<boolean>(false);
  const [syncPreferences, setSyncPreferences] = useState<SyncPreferences>({
    task: false,
    event: false
  });

  const connectedEmail = googleAuthData?.google_email || '';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isSuccess = urlParams.get('success') === 'true';
    const activityDetails = getLocalStorageItem(STORAGE_KEYS.ACTIVITY_DETAILS);

    if (isSuccess && activityDetails && activityDetails.type) {
      setShowCalendarIntegrationDetailsModal(true);
    } else if (!isSuccess && activityDetails && activityDetails.type) {
      if (activityDetails.type === 'task') {
        setActiveTab(ActivityTypeOptions.TASK);
        setIsModalOpen(true);
        setModalType(ActivityTypeOptions.TASK);
      } else if (activityDetails.type === 'event') {
        setActiveTab(ActivityTypeOptions.EVENT);
        setIsModalOpen(true);
        setModalType(ActivityTypeOptions.EVENT);
      } else if (activityDetails.type === 'call') {
        setActiveTab(ActivityTypeOptions.CALL);
        setIsModalOpen(true);
        setModalType(ActivityTypeOptions.CALL);
      }
    }
  }, []);

  const tabs: Tab[] = [
    {
      id: ActivityTypeOptions.TASK,
      label: 'Tasks',
      isActive: activeTab === ActivityTypeOptions.TASK
    },
    {
      id: ActivityTypeOptions.EVENT,
      label: 'Meetings',
      isActive: activeTab === ActivityTypeOptions.EVENT
    },
    {
      id: ActivityTypeOptions.CALL,
      label: 'Call Info',
      isActive: activeTab === ActivityTypeOptions.CALL
    }
  ];

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId as ActivityTypeOptions);
    setCurrentPage(1);
  };

  const handleSyncPreferenceChange = (
    type: SyncPreferenceType,
    value: boolean
  ) => {
    setSyncPreferences((prev) => ({
      ...prev,
      [type]: value
    }));
  };

  const handleAddActivity = (activityType: ActivityTypeOptions) => {
    setModalType(activityType);
    setIsEditMode(false);
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalType(ActivityTypeOptions.TASK);
    setIsEditMode(false);
    setEditingItem(null);
    localStorage.removeItem(STORAGE_KEYS.ACTIVITY_DETAILS);
  };

  const handleSubmit = () => {
    handleCloseModal();
  };

  const handleEditActivity = (itemId: string) => {
    let itemToEdit = null;
    let editModalType: ActivityTypeOptions = ActivityTypeOptions.TASK;

    if (activeTab === ActivityTypeOptions.TASK && tasks?.data) {
      itemToEdit = tasks.data.find((task) => task.id === itemId);
      editModalType = ActivityTypeOptions.TASK;
    } else if (activeTab === ActivityTypeOptions.EVENT && events?.data) {
      itemToEdit = events.data.find((event) => event.id === itemId);
      editModalType = ActivityTypeOptions.EVENT;
    } else if (activeTab === ActivityTypeOptions.CALL && calls?.data) {
      itemToEdit = calls.data.find((call) => call.id === itemId);
      editModalType = ActivityTypeOptions.CALL;
    }

    if (itemToEdit) {
      setEditingItem(itemToEdit as ActivityFormData);
      setModalType(editModalType);
      setIsEditMode(true);
      setIsModalOpen(true);
    }
  };

  const handleDeleteActivity = (itemId: string) => {
    let itemName = '';
    let itemType: ActivityTypeOptions = ActivityTypeOptions.TASK;
    let isLastItem = false;

    if (activeTab === ActivityTypeOptions.TASK && tasks?.data) {
      const task = tasks.data.find((t) => t.id === itemId);
      itemName = task?.name || '';
      itemType = ActivityTypeOptions.TASK;
      isLastItem = tasks.data.length === 1;
    } else if (activeTab === ActivityTypeOptions.EVENT && events?.data) {
      const event = events.data.find((e) => e.id === itemId);
      itemName = event?.name || '';
      itemType = ActivityTypeOptions.EVENT;
      isLastItem = events.data.length === 1;
    } else if (activeTab === ActivityTypeOptions.CALL && calls?.data) {
      itemType = ActivityTypeOptions.CALL;
      isLastItem = calls.data.length === 1;
    }

    setItemToDelete({
      id: itemId,
      type: itemType,
      name: itemName,
      isLastItem
    });
    setShowDeleteConfirmation(true);
  };

  const handleMenuClick = (itemId: string, action: 'edit' | 'delete') => {
    if (action === 'edit') {
      handleEditActivity(itemId);
    } else if (action === 'delete') {
      handleDeleteActivity(itemId);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === ActivityTypeOptions.TASK) {
        await deleteTask({
          organizationId,
          leadId,
          taskId: itemToDelete.id
        }).unwrap();

        // Mixpanel event for task deleted
        const taskToDelete = tasks?.data?.find((t) => t.id === itemToDelete.id);
        const { date: taskDate, time: taskTime } = splitTimestamp(
          taskToDelete?.timestamp || ''
        );
        const taskStatusForEvent = getTaskStatus(
          taskDate,
          taskTime,
          taskToDelete?.status || ''
        );
        sendMixpanelEvent(Event.LEAD_ACTIVITIES_TASK_DELETED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          lead_id: leadId,
          lead_name: leadDetails?.leadName,
          lead_phone: leadDetails?.leadPhone,
          task_id: itemToDelete.id,
          scheduled_datetime: taskToDelete?.timestamp,
          task_type: taskToDelete?.task_type,
          task_status: taskStatusForEvent
        });

        createSuccessAlert('Task deleted successfully');
        if (itemToDelete.isLastItem && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else if (itemToDelete.type === ActivityTypeOptions.EVENT) {
        const event = events?.data?.find((e) => e.id === itemToDelete.id);
        await deleteEvent({
          organizationId,
          leadId,
          eventId: itemToDelete.id
        }).unwrap();
        sendMixpanelEvent(Event.LEAD_ACTIVITIES_MEETINGS_DELETED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          lead_id: leadId,
          lead_name: leadDetails?.leadName,
          lead_phone: leadDetails?.leadPhone,
          meeting_id: itemToDelete.id,
          scheduled_datetime: {
            startDateTime: event?.start_time,
            endDateTime: event?.end_time
          }
        });
        createSuccessAlert('Meeting deleted successfully');
        if (itemToDelete.isLastItem && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else if (itemToDelete.type === ActivityTypeOptions.CALL) {
        const call = calls?.data?.find((c) => c.id === itemToDelete.id);
        await deleteCall({
          organizationId,
          leadId,
          callId: itemToDelete.id
        }).unwrap();
        sendMixpanelEvent(Event.LEAD_ACTIVITIES_CALL_INFO_DELETED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          lead_id: leadId,
          lead_name: leadDetails?.leadName,
          lead_phone: leadDetails?.leadPhone,
          call_info_id: itemToDelete.id,
          scheduled_datetime: call?.timestamp,
          call_type: call?.call_type
        });
        createSuccessAlert('Call Info deleted successfully');
        if (itemToDelete.isLastItem && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      }
    } catch (error) {
      createErrorAlert('Failed to delete item');
    }

    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleCloseGoogleCalendar = () => {
    setShowCalendarIntegrationDetailsModal(false);

    const url = new URL(window.location.href);
    url.searchParams.delete('success');
    window.history.replaceState({}, '', url.toString());

    const activityDetails = getLocalStorageItem(STORAGE_KEYS.ACTIVITY_DETAILS);
    if (activityDetails.type === 'task') {
      setActiveTab(ActivityTypeOptions.TASK);
      setIsModalOpen(true);
      setModalType(ActivityTypeOptions.TASK);
    } else if (activityDetails.type === 'event') {
      setActiveTab(ActivityTypeOptions.EVENT);
      setIsModalOpen(true);
      setModalType(ActivityTypeOptions.EVENT);
    } else if (activityDetails.type === 'call') {
      setActiveTab(ActivityTypeOptions.CALL);
      setIsModalOpen(true);
      setModalType(ActivityTypeOptions.CALL);
    }
  };

  const getInitialData = (): Partial<ActivityFormData> => {
    if (!editingItem) return {};

    switch (modalType) {
      case ActivityTypeOptions.TASK: {
        const { date: taskDate, time: taskTime } = splitTimestamp(
          editingItem.timestamp || ''
        );

        return {
          id: editingItem.id || '',
          name: editingItem.name || '',
          remarks: editingItem.remarks || '',
          taskType: editingItem.task_type,
          taskStatus: editingItem.status,
          date: taskDate,
          time: taskTime
        };
      }
      case ActivityTypeOptions.EVENT: {
        const { time: startTime } = splitTimestamp(
          editingItem.start_time || ''
        );
        const { time: endTime, date: endDate } = splitTimestamp(
          editingItem.end_time || ''
        );

        return {
          id: editingItem.id || '',
          name: editingItem.name || '',
          date: endDate,
          remarks: editingItem.remarks || '',
          startTime,
          endTime,
          location: editingItem.location || ''
        };
      }
      case ActivityTypeOptions.CALL: {
        const { date: callDate, time: callTime } = splitTimestamp(
          editingItem.timestamp || ''
        );

        return {
          id: editingItem.id || '',
          remarks: editingItem.remarks || '',
          call_type: editingItem.call_type,
          outcome: editingItem.outcome,
          date: callDate,
          time: callTime
        };
      }
      default:
        return {};
    }
  };

  const handleSave = async () => {
    try {
      await updateUserOrgSettings({
        organizationId,
        body: {
          task_calendar_sync_enabled: syncPreferences.task,
          event_calendar_sync_enabled: syncPreferences.event
        }
      }).unwrap();

      refetchSettings();
      setIsModalOpen(false);
      createSuccessAlert('Sync preferences updated successfully!');
      handleCloseGoogleCalendar();
    } catch (error) {
      createErrorAlert('Failed to update sync preferences.');
      handleCloseGoogleCalendar();
    }
  };

  const isSaveDisabled = () => {
    if (!googleAuthData || !userOrgSettingsData) return true;

    const apiPreferences = {
      task: userOrgSettingsData?.task_calendar_sync_enabled,
      event: userOrgSettingsData?.event_calendar_sync_enabled
    };

    return (
      syncPreferences.task === apiPreferences.task &&
      syncPreferences.event === apiPreferences.event
    );
  };

  useEffect(() => {
    if (userOrgSettingsData) {
      setSyncPreferences({
        task: userOrgSettingsData?.task_calendar_sync_enabled,
        event: userOrgSettingsData?.event_calendar_sync_enabled
      });
    }
  }, [userOrgSettingsData]);

  const getAddButtonLabel = () => {
    switch (activeTab) {
      case ActivityTypeOptions.TASK:
        return 'Add Task';
      case ActivityTypeOptions.EVENT:
        return 'Add Meeting';
      case ActivityTypeOptions.CALL:
        return 'Add Call Info';
      default:
        return 'Add Task';
    }
  };

  return (
    <div className="rounded-r-xl bg-white p-6 rounded-b-xl">
      <div className="flex items-center justify-between">
        <TabNavigation tabs={tabs} onTabClick={handleTabClick} />

        <Button
          label={getAddButtonLabel()}
          handleClick={() => handleAddActivity(activeTab)}
          className="h-9 border border-[#00A657] !bg-[#EDFFF7] px-4 text-[#00A657]"
          leftIconName="add-green"
          leftIconStyle="!size-4"
          variant="transparent"
        />
      </div>

      <div className="mt-6">
        {activeTab === ActivityTypeOptions.TASK && (
          <TaskTable
            handleMenuIconClick={handleMenuClick}
            tasks={tasks?.data || []}
            page={currentPage}
            totalCount={tasks?.pagination?.total_rows || 0}
            setPage={setCurrentPage}
          />
        )}

        {activeTab === ActivityTypeOptions.EVENT && (
          <EventTable
            handleMenuIconClick={handleMenuClick}
            events={events?.data || []}
            page={currentPage}
            totalCount={events?.pagination?.total_rows || 0}
            setPage={setCurrentPage}
          />
        )}

        {activeTab === ActivityTypeOptions.CALL && (
          <CallTable
            handleMenuIconClick={handleMenuClick}
            calls={calls?.data || []}
            page={currentPage}
            totalCount={calls?.pagination?.total_rows || 0}
            setPage={setCurrentPage}
          />
        )}
      </div>

      {isModalOpen && (
        <ActivityModal
          onClose={handleCloseModal}
          type={modalType}
          onSubmit={handleSubmit}
          leadId={leadId}
          analyticsData={analyticsData}
          initialData={isEditMode ? getInitialData() : {}}
        />
      )}

      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          showDeleteModal={showDeleteConfirmation}
          title={getDeleteModalTitle(itemToDelete)}
          message={getDeleteModalMessage(itemToDelete)}
          handleConfirmDelete={handleConfirmDelete}
          handleCancel={handleCancelDelete}
        />
      )}
      {showCalendarIntegrationDetailsModal && (
        <CalendarIntegrationDetailsModal
          isOpen={showCalendarIntegrationDetailsModal}
          onClose={handleCloseGoogleCalendar}
          syncPreferences={syncPreferences}
          onSyncPreferenceChange={handleSyncPreferenceChange}
          onSave={handleSave}
          connectedEmail={connectedEmail}
          isSaveDisabled={isSaveDisabled()}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default ActivitiesTab;
